import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Palette as PaletteIcon,
  DarkMode,
  LightMode,
  AutoMode,
  Close as CloseIcon,
  Brush,
  ColorLens,
  Gradient
} from '@mui/icons-material';
import { useAdvancedTheme } from './AdvancedThemeProvider.helpers.js';
import { useTranslation } from 'react-i18next';

/**
 * Theme Customizer Component
 * 
 * Theme customization interface with:
 * - Dark/light mode toggle
 * - Palette selection
 * - Auto theme mode
 * - Color customization
 * - Theme preview
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @returns {JSX.Element} The theme customizer component
 */
const ThemeCustomizer = ({ open, onClose }) => {
  const { t } = useTranslation(['theme', 'common']);

  const {
    isDarkMode,
    currentPalette,
    isAutoTheme,
    availablePalettes,
    toggleTheme,
    changePalette,
    toggleAutoTheme,
    currentPaletteColors
  } = useAdvancedTheme();

  const paletteDescriptions = {
    standard: {
      name: t('theme.palettes.standard.name'),
      description: t('theme.palettes.standard.description'),
      icon: '🏢',
      mood: t('theme.palettes.standard.mood')
    },
    cyberpunk: {
      name: t('theme.palettes.cyberpunk.name'),
      description: t('theme.palettes.cyberpunk.description'),
      icon: '🌆',
      mood: t('theme.palettes.cyberpunk.mood')
    },
    neon: {
      name: t('theme.palettes.neon.name'),
      description: t('theme.palettes.neon.description'),
      icon: '🎮',
      mood: t('theme.palettes.neon.mood')
    },
    aurora: {
      name: t('theme.palettes.aurora.name'),
      description: t('theme.palettes.aurora.description'),
      icon: '🌌',
      mood: t('theme.palettes.aurora.mood')
    },
    holographic: {
      name: t('theme.palettes.holographic.name'),
      description: t('theme.palettes.holographic.description'),
      icon: '💎',
      mood: t('theme.palettes.holographic.mood')
    }
  };

  const PalettePreview = ({ paletteName, colors, isSelected, onClick }) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        border: isSelected ? `2px solid ${colors.primary}` : '1px solid rgba(255,255,255,0.1)',
        background: `linear-gradient(135deg, ${colors.surface}80, ${colors.background}60)`,
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: `0 8px 24px ${colors.glow}`
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {paletteDescriptions[paletteName].icon} {paletteDescriptions[paletteName].name}
          </Typography>
          {isSelected && (
            <Chip 
              label={t('common.active', 'Active')} 
              size="small" 
              sx={{ 
                background: colors.primary + '40',
                color: colors.primary,
                fontSize: '0.7rem'
              }} 
            />
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.8rem' }}>
          {paletteDescriptions[paletteName].description}
        </Typography>
        
        <Box display="flex" gap={1} mb={1}>
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              background: colors.primary,
              boxShadow: `0 0 10px ${colors.primary}80`
            }} 
          />
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              background: colors.secondary,
              boxShadow: `0 0 10px ${colors.secondary}80`
            }} 
          />
          <Box 
            sx={{ 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              background: colors.accent,
              boxShadow: `0 0 10px ${colors.accent}80`
            }} 
          />
        </Box>
        
        <Chip 
          label={paletteDescriptions[paletteName].mood}
          size="small"
          sx={{ 
            background: colors.accent + '20',
            color: colors.accent,
            fontSize: '0.7rem'
          }}
        />
      </CardContent>
    </Card>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: isDarkMode 
            ? `linear-gradient(135deg, ${currentPaletteColors?.surface || '#1a1a1a'}90, ${currentPaletteColors?.background || '#0a0a0a'}70)`
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode 
            ? `1px solid ${currentPaletteColors?.primary || '#00ffff'}30`
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '20px',
          boxShadow: isDarkMode 
            ? `0 20px 60px ${currentPaletteColors?.glow || 'rgba(0, 255, 255, 0.3)'}`
            : '0 20px 60px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <PaletteIcon sx={{ 
            color: currentPaletteColors?.primary || 'primary.main',
            filter: `drop-shadow(0 0 10px ${currentPaletteColors?.primary || '#2563eb'}80)`
          }} />
          <Typography variant="h5" sx={{ 
            background: isDarkMode 
              ? `linear-gradient(45deg, ${currentPaletteColors?.primary}, ${currentPaletteColors?.secondary})`
              : 'linear-gradient(45deg, #2563eb, #7c3aed)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}
        >
          {t('theme.customizer.title')}
        </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={t('common.close', 'Close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Controles principaux */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Brush /> {t('theme.customizer.displayModes')}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={isDarkMode && !isAutoTheme} 
                    onChange={toggleTheme}
                    disabled={isAutoTheme}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <DarkMode fontSize="small" />
                    {t('theme.modes.dark')}
                  </Box>
                }
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={!isDarkMode && !isAutoTheme} 
                    onChange={toggleTheme}
                    disabled={isAutoTheme}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <LightMode fontSize="small" />
                    {t('theme.modes.light')}
                  </Box>
                }
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={isAutoTheme} 
                    onChange={toggleAutoTheme}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <AutoMode fontSize="small" />
                    {t('theme.modes.auto')}
                  </Box>
                }
              />
            </Grid>
          </Grid>
          
          {isAutoTheme && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              {t('theme.customizer.autoModeDescription')}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Color palettes */}
        <Box>
          <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ColorLens /> {t('theme.customizer.palettesTitle')}
          </Typography>
          
          <Grid container spacing={2}>
            {availablePalettes.map((palette) => (
              <Grid item xs={12} sm={6} md={3} key={palette}>
                <PalettePreview
                  paletteName={palette}
                  colors={{
                    primary: '#00ffff',
                    secondary: '#ff00ff',
                    accent: '#ffff00',
                    background: '#0a0a0a',
                    surface: '#1a1a1a',
                    glow: 'rgba(0, 255, 255, 0.3)',
                    ...({
                      cyberpunk: { primary: '#00ffff', secondary: '#ff00ff', accent: '#ffff00' },
                      neon: { primary: '#ff0080', secondary: '#00ff80', accent: '#8000ff' },
                      aurora: { primary: '#00ff9f', secondary: '#ff6b9d', accent: '#4ecdc4' },
                      holographic: { primary: '#7c3aed', secondary: '#06b6d4', accent: '#f59e0b' }
                    }[palette])
                  }}
                  isSelected={currentPalette === palette}
                  onClick={() => changePalette(palette)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Real-time preview */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gradient /> {t('theme.customizer.livePreview')}
          </Typography>
          
          <Card sx={{ 
            background: isDarkMode 
              ? `linear-gradient(135deg, ${currentPaletteColors?.surface}60, ${currentPaletteColors?.background}40)`
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            border: isDarkMode 
              ? `1px solid ${currentPaletteColors?.primary}20`
              : '1px solid rgba(0, 0, 0, 0.1)',
            p: 2
          }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('theme.customizer.currentTheme')}: <strong>{isDarkMode ? t('theme.modes.dark') : t('theme.modes.light')}</strong>
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('theme.customizer.currentPalette')}: <strong>{paletteDescriptions[currentPalette].name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {paletteDescriptions[currentPalette].description}
            </Typography>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            px: 3
          }}
        >
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ThemeCustomizer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default React.memo(ThemeCustomizer);

